import { useRef, useState } from 'react';

import { Box, Collapse, Paper, Stack, Typography } from '@mui/material';

import type {
  ConversationalFormExplanation,
  ConversationalFormOption,
} from '@karehero/models';

import { ChatActionRow } from '@/pwa/molecules/ChatActionRow/ChatActionRow';

/** More answers than this start behind See options, so the question is read before them. */
export const MENU_ATTACH_MAX = 4;

/** How long the answers not picked take to fold away. */
export const MENU_FOLD_MS = 240;

const SEE_OPTIONS = 'See options';

export interface ChatMenuMessageProps {
  text: string;
  /** Answers first; quieter ones (`isQuiet`) follow them. */
  options: ConversationalFormOption[];
  pickedId?: string;
  /** Answered some other way: the answers fold away, leaving the question. */
  isClosed?: boolean;
  /** Omit once it is no longer the question being asked. */
  onChoose?: (option: ConversationalFormOption) => void;
  /** Omit where the answer cannot be changed; offered once answered. */
  onChange?: (option: ConversationalFormOption) => void;
  /** Opens what a row that explains, rather than answers, has to say. */
  onExplain?: (explanation: ConversationalFormExplanation) => void;
}

const isQuiet = (option: ConversationalFormOption) => !!option.isQuiet;

/**
 * ChatMenuMessage is a question with its answers attached under it. Once
 * answered the rest fold away, leaving the one picked, so the history reads as
 * decided; where the answer can be changed, tapping it opens them again.
 */
export const ChatMenuMessage = ({
  text,
  options,
  pickedId,
  isClosed = false,
  onChoose,
  onChange,
  onExplain,
}: ChatMenuMessageProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const answers = options.filter((option) => !isQuiet(option));
  const quiet = options.filter(isQuiet);
  const isBehind = answers.length > MENU_ATTACH_MAX;
  const [isOpened, setIsOpened] = useState(false);
  const [isReopened, setIsReopened] = useState(false);
  const isAnswered = !!pickedId || isClosed;
  const isOut = !isBehind || isOpened || isAnswered;

  const showAll = () => {
    setIsOpened(true);
    // Once they open, the chat moves to show them.
    window.setTimeout(
      () =>
        ref.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' }),
      MENU_FOLD_MS,
    );
  };

  const pickAnswer = (option: ConversationalFormOption) => {
    if (!isAnswered) {
      onChoose?.(option);
      return;
    }
    if (!isReopened) {
      setIsReopened(true);
      return;
    }
    setIsReopened(false);
    if (option.id !== pickedId) onChange?.(option);
  };

  const pickQuiet = (option: ConversationalFormOption) => {
    if (option.explain) onExplain?.(option.explain);
    else if (!isAnswered) onChoose?.(option);
  };

  const isAnswerShown = (option: ConversationalFormOption) =>
    isOut && (!isAnswered || isReopened || option.id === pickedId);
  const isAnswerLive = (option: ConversationalFormOption) =>
    isAnswered
      ? !!onChange && (isReopened || option.id === pickedId)
      : !!onChoose;

  // Kept once answered where they explain something to come back to.
  const isQuietShown = (option: ConversationalFormOption) =>
    !isAnswered || !!option.explain?.isKept;

  return (
    <Stack ref={ref} role='group' aria-label={text} sx={{ width: '100%' }}>
      <Paper variant='chatBubbleOutlined'>
        <Typography variant='body1' sx={{ whiteSpace: 'pre-line' }}>
          {text}
        </Typography>
      </Paper>
      {isBehind && (
        <Collapse in={!isOut} timeout={MENU_FOLD_MS}>
          <Box sx={{ pt: 0.5 }}>
            <ChatActionRow
              label={SEE_OPTIONS}
              icon='list'
              tone='menu'
              isDisabled={!onChoose}
              onClick={showAll}
            />
          </Box>
        </Collapse>
      )}
      {answers.map((option) => (
        // The gap is inside the fold, so a folded row takes its gap with it.
        <Collapse
          key={option.id}
          in={isAnswerShown(option)}
          timeout={MENU_FOLD_MS}
        >
          <Box sx={{ pt: 0.5 }}>
            <ChatActionRow
              label={option.label}
              icon={option.id === pickedId ? 'check' : undefined}
              tone='menu'
              isChosen={option.id === pickedId}
              isDisabled={!isAnswerLive(option)}
              isReadOnly={isAnswered && !onChange}
              onClick={() => pickAnswer(option)}
            />
          </Box>
        </Collapse>
      ))}
      {quiet.map((option) => (
        <Collapse
          key={option.id}
          in={isQuietShown(option)}
          timeout={MENU_FOLD_MS}
        >
          <Box sx={{ pt: 0.5 }}>
            <ChatActionRow
              label={option.label}
              tone='menu'
              isQuiet
              isDisabled={option.explain ? !onExplain : isAnswered || !onChoose}
              onClick={() => pickQuiet(option)}
            />
          </Box>
        </Collapse>
      ))}
    </Stack>
  );
};

ChatMenuMessage.displayName = 'ChatMenuMessage';
export default ChatMenuMessage;
